import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { formatCurrency, toLocalDate } from '@/lib/formatters';
import type { PositionWithLegs } from '@steward/shared';

interface ChainFlowProps {
  positions: PositionWithLegs[];
}

type SegmentStatus = 'active' | 'rolled' | 'expired' | 'closed';

interface GanttSegment {
  id: string;
  positionId: string;
  rollNumber: number;
  side: 'call' | 'put';
  status: SegmentStatus;
  startDate: Date;
  endDate: Date; // close date, expiry date, or today if active
  strikes: string;
  expLabel: string;
  premium: number;
  closeReason: string | null;
}

interface GanttLane {
  side: 'call' | 'put';
  segments: GanttSegment[];
}

function formatShortDate(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function buildGanttData(positions: PositionWithLegs[]): { lanes: GanttLane[]; minDate: Date; maxDate: Date } {
  const sorted = [...positions].sort((a, b) => a.rollNumber - b.rollNumber);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const callSegments: GanttSegment[] = [];
  const putSegments: GanttSegment[] = [];

  for (const pos of sorted) {
    const callLegs = pos.legs.filter(l => l.optionType === 'call');
    const putLegs = pos.legs.filter(l => l.optionType === 'put');

    for (const [legs, segments, side] of [
      [callLegs, callSegments, 'call'] as const,
      [putLegs, putSegments, 'put'] as const,
    ]) {
      if (legs.length === 0) continue;

      const strikes = legs
        .map(l => l.strikePrice)
        .sort((a, b) => a - b);
      const strikeLabel = strikes.length === 1
        ? `${strikes[0]}${side === 'call' ? 'C' : 'P'}`
        : `${strikes[0]}/${strikes[1]}${side === 'call' ? 'C' : 'P'}`;

      const expDate = toLocalDate(legs[0].expirationDate);
      const expLabel = `${expDate.getMonth() + 1}/${expDate.getDate()}`;

      const allExpired = legs.every(l => l.closeReason === 'expired');
      const allClosed = legs.every(l => l.quantityRemaining === 0);
      const isActive = !allClosed;

      let status: SegmentStatus;
      if (isActive) {
        status = 'active';
      } else if (allExpired) {
        status = 'expired';
      } else if (pos.closeReason === 'rolled' || (allClosed && !allExpired && segments.length > 0)) {
        // Closed but not expired, and there are prior segments = likely rolled
        status = 'rolled';
      } else {
        status = 'closed';
      }

      // If this is not the last segment on this side and it's closed, it was rolled
      // We'll fix this after building all segments

      const startDate = new Date(pos.openedAt);
      startDate.setHours(0, 0, 0, 0);

      let endDate: Date;
      if (isActive) {
        // Active: show to today or expiration, whichever is later (for visual span)
        endDate = today > expDate ? today : expDate;
      } else if (allExpired) {
        endDate = expDate;
      } else if (pos.closedAt) {
        endDate = new Date(pos.closedAt);
        endDate.setHours(0, 0, 0, 0);
      } else {
        endDate = expDate;
      }

      // Ensure minimum 1-day visual width
      if (endDate <= startDate) {
        endDate = new Date(startDate.getTime() + 24 * 60 * 60 * 1000);
      }

      const premium = legs.reduce((sum, l) => {
        const val = l.pricePerContract * l.quantity * 100;
        return sum + (l.action === 'STO' || l.action === 'STC' ? val : -val);
      }, 0);

      segments.push({
        id: `${pos.id}-${side}`,
        positionId: pos.id,
        rollNumber: pos.rollNumber,
        side,
        status,
        startDate,
        endDate,
        strikes: strikeLabel,
        expLabel,
        premium,
        closeReason: pos.closeReason,
      });
    }
  }

  // Mark non-final closed segments as "rolled"
  for (const segments of [callSegments, putSegments]) {
    for (let i = 0; i < segments.length - 1; i++) {
      if (segments[i].status === 'closed') {
        segments[i].status = 'rolled';
      }
    }
  }

  const lanes: GanttLane[] = [];
  if (callSegments.length > 0) lanes.push({ side: 'call', segments: callSegments });
  if (putSegments.length > 0) lanes.push({ side: 'put', segments: putSegments });

  // Calculate global date range
  const allDates = [...callSegments, ...putSegments].flatMap(s => [s.startDate, s.endDate]);
  const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
  const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));

  // Add padding
  minDate.setDate(minDate.getDate() - 2);
  maxDate.setDate(maxDate.getDate() + 5);

  return { lanes, minDate, maxDate };
}

const segmentColors: Record<SegmentStatus, { bg: string; border: string; text: string; hoverBg: string }> = {
  active: { bg: 'bg-emerald-100', border: 'border-emerald-400', text: 'text-emerald-800', hoverBg: 'hover:bg-emerald-200' },
  rolled: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', hoverBg: 'hover:bg-amber-100' },
  expired: { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-500', hoverBg: 'hover:bg-gray-200' },
  closed: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-700', hoverBg: 'hover:bg-blue-100' },
};

const statusLabels: Record<SegmentStatus, string> = {
  active: 'Active',
  rolled: 'Rolled',
  expired: 'Expired',
  closed: 'Closed',
};

export function ChainFlow({ positions }: ChainFlowProps) {
  const { lanes, minDate, maxDate } = useMemo(() => buildGanttData(positions), [positions]);
  const [selectedSegment, setSelectedSegment] = useState<string | null>(null);

  if (lanes.length === 0) return null;

  const totalDays = Math.max(1, (maxDate.getTime() - minDate.getTime()) / (24 * 60 * 60 * 1000));

  function getPosition(date: Date): number {
    const days = (date.getTime() - minDate.getTime()) / (24 * 60 * 60 * 1000);
    return (days / totalDays) * 100;
  }

  function getWidth(start: Date, end: Date): number {
    const days = Math.max(1, (end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
    return Math.max(2, (days / totalDays) * 100);
  }

  // Generate month markers for the time axis
  const monthMarkers: Array<{ date: Date; label: string; position: number }> = [];
  const cursor = new Date(minDate);
  cursor.setDate(1);
  cursor.setMonth(cursor.getMonth() + 1);
  while (cursor < maxDate) {
    monthMarkers.push({
      date: new Date(cursor),
      label: cursor.toLocaleDateString('en-US', { month: 'short' }),
      position: getPosition(cursor),
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }

  // Today marker
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayPos = today >= minDate && today <= maxDate ? getPosition(today) : null;

  const selected = selectedSegment
    ? [...lanes.flatMap(l => l.segments)].find(s => s.id === selectedSegment)
    : null;

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Position Timeline</div>

      {/* Time axis */}
      <div className="relative h-5 ml-12">
        {monthMarkers.map(m => (
          <div
            key={m.label}
            className="absolute text-[10px] text-gray-400"
            style={{ left: `${m.position}%` }}
          >
            {m.label}
          </div>
        ))}
      </div>

      {/* Lanes */}
      {lanes.map(lane => (
        <div key={lane.side} className="flex items-center">
          {/* Lane label */}
          <div className={cn(
            'w-12 text-xs font-semibold text-right pr-2 flex-shrink-0',
            lane.side === 'call' ? 'text-blue-600' : 'text-red-500'
          )}>
            {lane.side === 'call' ? 'Calls' : 'Puts'}
          </div>

          {/* Timeline bar */}
          <div className="relative flex-1 h-10 bg-gray-50 rounded border border-gray-100">
            {/* Today marker */}
            {todayPos !== null && (
              <div
                className="absolute top-0 bottom-0 w-px bg-gray-300 z-10"
                style={{ left: `${todayPos}%` }}
              />
            )}

            {/* Segments */}
            {lane.segments.map(seg => {
              const left = getPosition(seg.startDate);
              const width = getWidth(seg.startDate, seg.endDate);
              const colors = segmentColors[seg.status];
              const isSelected = selectedSegment === seg.id;

              return (
                <button
                  key={seg.id}
                  className={cn(
                    'absolute top-1 bottom-1 rounded border text-left px-1.5 overflow-hidden transition-all cursor-pointer',
                    colors.bg,
                    colors.border,
                    colors.hoverBg,
                    isSelected && 'ring-2 ring-offset-1 ring-blue-400'
                  )}
                  style={{ left: `${left}%`, width: `${width}%`, minWidth: '40px' }}
                  onClick={() => setSelectedSegment(isSelected ? null : seg.id)}
                  title={`${seg.strikes} (${statusLabels[seg.status]}) ${formatShortDate(seg.startDate)} – ${formatShortDate(seg.endDate)}`}
                >
                  <div className="flex items-center gap-1 h-full">
                    <span className={cn('text-[11px] font-bold truncate', colors.text)}>
                      {seg.strikes}
                    </span>
                    {width > 8 && (
                      <span className="text-[9px] text-gray-400 truncate">
                        {seg.expLabel}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Selected segment detail */}
      {selected && (
        <div className={cn(
          'ml-12 rounded-lg border p-3 text-sm',
          segmentColors[selected.status].bg,
          segmentColors[selected.status].border
        )}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={cn('text-[10px] font-semibold uppercase', segmentColors[selected.status].text)}>
                {statusLabels[selected.status]}
              </span>
              <span className={cn('font-bold', selected.side === 'call' ? 'text-blue-600' : 'text-red-500')}>
                {selected.strikes}
              </span>
              <span className="text-gray-500">exp {selected.expLabel}</span>
            </div>
            <span className={cn(
              'text-xs font-medium',
              selected.premium >= 0 ? 'text-green-600' : 'text-red-600'
            )}>
              {formatCurrency(selected.premium, { showSign: true })}
            </span>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {formatShortDate(selected.startDate)} – {formatShortDate(selected.endDate)}
            {selected.status === 'active' && ' (open)'}
          </div>
        </div>
      )}
    </div>
  );
}
